// Entity-merge AUDIT log API — reads the `entity_merge_audit` table and reverts
// a recorded merge (recreate the deleted node + re-wire its surviving edges).
import { apiClient } from './client'

export interface AuditSummary {
  id: string
  created_at: string | null
  /** auto_same_document | manual | fuzzy_cross_document | set_canonical | … */
  merge_reason: string
  triggered_by: string
  canonical_node_id: string
  canonical_node_key: string | null
  merged_node_id: string
  merged_node_name: string | null
  merged_node_labels: string[]
  similarity_score: number | null
  relationship_count: number
  reverted_at: string | null
  reverted_by: string | null
}

export interface AuditRelationship {
  type: string
  direction: 'OUT' | 'IN'
  other_node_id: string
  other_node_labels: string[]
  properties: Record<string, unknown>
}

export interface AuditMatchBasis {
  property: string
  merged_value: unknown
  canonical_value: unknown
  score: number
}

export interface CanonicalPreview {
  exists: boolean
  labels: string[]
  display_name: string | null
  properties: Record<string, unknown>
  successor: { audit_id: string; node_id: string; display_name: string | null } | null
}

export interface AuditDetail extends AuditSummary {
  case_id: string
  cluster_id: string | null
  merged_node_properties: Record<string, unknown>
  merged_node_relationships: AuditRelationship[]
  workflow_run_id: string | null
  match_basis: AuditMatchBasis[] | null
  canonical_preview: CanonicalPreview
  /** Current schema hints for legacy records; never historical match evidence. */
  unique_property_hints?: string[]
}

export interface AuditPage {
  records: AuditSummary[]
  total: number
}

export interface RevertResult {
  restored_id: string
  reattached: number
  skipped: number
  total_edges: number
  /** Edges removed from the canonical — ones it had only absorbed from this victim. */
  survivor_edges_removed: number
  /** Props removed from the canonical — ones it had only inherited from this victim. */
  survivor_props_removed: number
}

export const auditApi = {
  list: (
    caseId: string,
    options: { limit: number; offset: number; includeReverted?: boolean; search?: string }
  ) =>
    apiClient.get<AuditPage>(`/v1/cases/${caseId}/entity-resolution/audit`, {
      params: {
        limit: options.limit,
        offset: options.offset,
        include_reverted: options.includeReverted === false ? 'false' : 'true',
        ...(options.search ? { search: options.search } : {}),
      },
    }),

  get: (caseId: string, auditId: string) =>
    apiClient.get<AuditDetail>(`/v1/cases/${caseId}/entity-resolution/audit/${auditId}`),

  revert: (caseId: string, auditId: string) =>
    apiClient.post<RevertResult>(`/v1/cases/${caseId}/entity-resolution/audit/${auditId}/revert`),
}
